import { useEffect, useRef, useState } from "react";
import { AddOutlined, CloseOutlined, DeleteOutlineOutlined, EditOutlined, SaveOutlined } from "@mui/icons-material";
import { FormControl, Select, MenuItem, Switch } from "@mui/material";
import { createSubscriptionPlan, getSubscriptionPlans, getSubscriptionPricePoints, updateSubscriptionPlan } from "../../../../services/Dashboard/Table";

const EMPTY_PLAN_FORM = {
  name: "", description: "", price: "0.00", currency: "AUD",
  period_days: 30, max_races: "", is_active: true,
  features: [""], most_popular: false,
  ios_product_id: "", android_product_id: "",
  show_hot_picks: true, auto_provision: true,
};

export function getPlanErrorMessage(error, fallback) {
  if (!error) return fallback;
  if (typeof error.error === "string") return error.error;
  if (typeof error.detail === "string") return error.detail;
  const firstFieldError = Object.entries(error).find(([, value]) => value);
  if (!firstFieldError) return fallback;
  const [field, value] = firstFieldError;
  const message = Array.isArray(value) ? value.join(" ") : String(value);
  return `${field.replaceAll("_", " ")}: ${message}`;
}

function formatPlanPrice(plan) {
  const amount = Number(plan.price || 0);
  return `A$${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)}`;
}

function PlanStatusPill({ active }) {
  return (
    <span className={`inline-flex items-center justify-center min-h-7 px-2.5 py-2 rounded-full border md:text-xs text-[10px] font-extrabold uppercase tracking-[0.04em] ${active ? "border-[rgba(200,255,0,0.25)] bg-[rgba(200,255,0,0.12)] text-[var(--color-primary)]" : "border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] text-[var(--color-accent-red)]"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

const switchSx = { "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--color-primary)" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "var(--color-primary)" } };
const selectSx = {
  minHeight: 48,
  color: "var(--color-text)",
  fontSize: { xs: 12, sm: 14, lg: 16 },
  fontWeight: 600,
  background: "var(--color-bg)",
  borderRadius: "var(--radius-md)",
  "& .MuiSelect-select": { minHeight: "20px", padding: "12px 32px 12px 16px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-border)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(200,255,0,0.25)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(200,255,0,0.25)" },
  "& .MuiSvgIcon-root": { color: "var(--color-text-muted)" }
};

function PlanForm({ form, setForm, editingPlan, saving, onSubmit, onCancel, formRef, firstInputRef, pricePoints }) {
  const fieldClass = "w-full px-4 py-3 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-bg)] text-[var(--color-text)] xl:text-base md:text-sm text-xs outline-none";
  const labelClass = "grid gap-2 text-[var(--color-text-muted)] md:text-sm text-xs font-bold uppercase tracking-[0.04em]";

  const [manualIos, setManualIos] = useState(false);
  const [manualAndroid, setManualAndroid] = useState(false);

  useEffect(() => {
    if (editingPlan) {
      setManualIos(Boolean(editingPlan.ios_product_id));
      setManualAndroid(Boolean(editingPlan.android_product_id));
    } else {
      setManualIos(false);
      setManualAndroid(false);
    }
  }, [editingPlan]);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const generateProductSuffix = (planName, maxSuffixChars = 27) => {
    return (planName || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ".")
      .replace(/^\.+|\.+$/g, "")
      .slice(0, maxSuffixChars)
      .replace(/\.+$/g, "");
  };

  const handleNameChange = (newName) => {
    setForm((current) => {
      const iosSuffix = generateProductSuffix(newName, 87); // 13 + 87 = 100 max
      const androidSuffix = generateProductSuffix(newName, 27); // 13 + 27 = 40 max
      const autoIosId = iosSuffix ? `com.formguru.${iosSuffix}` : "";
      const autoAndroidId = androidSuffix ? `com.formguru.${androidSuffix}` : "";
      return {
        ...current,
        name: newName,
        ios_product_id: manualIos ? current.ios_product_id : autoIosId,
        android_product_id: manualAndroid ? current.android_product_id : autoAndroidId,
      };
    });
  };

  const handleIosIdChange = (value) => {
    setManualIos(true);
    // Apple App Store condition: max 100 characters. Auto-slice first 100 characters if exceeded
    const sanitized = value.slice(0, 100);
    updateField("ios_product_id", sanitized);
  };

  const handleAndroidIdChange = (value) => {
    setManualAndroid(true);
    // Google Play condition: max 40 characters. Auto-slice first 40 characters if exceeded
    const sanitized = value.slice(0, 40);
    updateField("android_product_id", sanitized);
  };

  const addFeature = () => {
    setForm((current) => ({
      ...current,
      features: [...(current.features || [""]), ""],
    }));
  };

  const removeFeature = (index) => {
    setForm((current) => {
      const currentFeatures = current.features || [""];
      if (currentFeatures.length <= 1) {
        return { ...current, features: [""] };
      }
      const newFeatures = [...currentFeatures];
      newFeatures.splice(index, 1);
      return { ...current, features: newFeatures };
    });
  };

  const updateFeature = (index, value) => {
    setForm((current) => {
      const newFeatures = [...(current.features || [""])];
      newFeatures[index] = value;
      return { ...current, features: newFeatures };
    });
  };

  return (
    <form ref={formRef} onSubmit={onSubmit} className="grid gap-4 p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)]">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <p className="md:text-sm text-xs label-caps block m-0">{editingPlan ? "Edit Plan" : "New Plan"}</p>
          <h4 className="xl:text-xl md:text-lg text-base text-[var(--color-text)]">{editingPlan ? editingPlan.name : "Create subscription plan"}</h4>
        </div>
        {editingPlan && (
          <button type="button" onClick={onCancel} className="inline-flex items-center gap-1.5 min-h-[38px] px-3 py-2.5 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-transparent text-[var(--color-text-muted)] cursor-pointer md:text-sm text-xs font-bold">
            <CloseOutlined sx={{ fontSize: 17 }} />Cancel
          </button>
        )}
      </div>

      <div className="admin-plan-form-grid grid xl:grid-cols-2 md:grid-cols-2 grid-cols-1 gap-3 items-start">
        <label className={labelClass}>Name<input ref={firstInputRef} value={form.name} onChange={(event) => handleNameChange(event.target.value)} required className={fieldClass} placeholder="e.g. Pro Monthly" /></label>
        <label className={labelClass}>Price
          <FormControl size="small" sx={{ minWidth: 0, width: "100%" }}>
            <Select
              value={Number(form.price || 0).toFixed(2)}
              onChange={(event) => {
                const newPrice = event.target.value;
                setForm((current) => {
                  let newDuration = current.period_days;
                  if (Number(newPrice) > 0 && ![7, 30, 60, 90, 180, 365].includes(Number(newDuration))) {
                    newDuration = 30; // default to 1 month if snapping to standard duration
                  }
                  return { ...current, price: newPrice, period_days: newDuration };
                });
              }}
              aria-label="Price"
              MenuProps={{ PaperProps: { sx: { backgroundColor: "var(--color-surface)", color: "var(--color-text)", border: "1px solid var(--color-border)", maxHeight: 300, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } } } }}
              sx={selectSx}
            >
              {pricePoints.map((pt) => (
                <MenuItem key={pt.id} value={Number(pt.customer_price).toFixed(2)}>
                  {pt.customer_price_formatted} AUD
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </label>
        <label className={labelClass}>Duration (Days)
          {(() => {
            const isDurationLocked = !!editingPlan && Number(editingPlan.price) > 0 && Number(form.price) > 0;
            return (
              <div className="flex flex-col gap-1.5">
                {Number(form.price) === 0 ? (
                  <input type="number" min="1" value={form.period_days} onChange={(event) => updateField("period_days", Number(event.target.value))} required className={fieldClass} placeholder="e.g. 30" />
                ) : (
                  <FormControl size="small" sx={{ minWidth: 0, width: "100%" }}>
                    <Select
                      value={form.period_days}
                      disabled={isDurationLocked}
                      onChange={(event) => updateField("period_days", Number(event.target.value))}
                      aria-label="Duration"
                      MenuProps={{ PaperProps: { sx: { backgroundColor: "var(--color-surface)", color: "var(--color-text)", border: "1px solid var(--color-border)" } } }}
                      sx={{
                        ...selectSx,
                        "&.Mui-disabled": {
                          opacity: 1,
                          cursor: "not-allowed",
                          pointerEvents: "auto",
                          "& .MuiSelect-select": { 
                            color: "var(--color-text-muted)", 
                            WebkitTextFillColor: "var(--color-text-muted)",
                            cursor: "not-allowed"
                          },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-border)" },
                          "& .MuiSvgIcon-root": { color: "var(--color-text-muted)" },
                        },
                      }}
                    >
                      <MenuItem value={7}>1 week (7 days)</MenuItem>
                      <MenuItem value={30}>1 month (30 days)</MenuItem>
                      <MenuItem value={60}>2 months (60 days)</MenuItem>
                      <MenuItem value={90}>3 months (90 days)</MenuItem>
                      <MenuItem value={180}>6 months (180 days)</MenuItem>
                      <MenuItem value={365}>1 year (365 days)</MenuItem>
                    </Select>
                  </FormControl>
                )}
                {isDurationLocked && (
                  <p className="m-0 text-xs text-[var(--color-text-muted)] font-normal normal-case">
                    Duration cannot be modified for existing paid store plans
                  </p>
                )}
              </div>
            );
          })()}
        </label>
        <label className={labelClass}>Max Races Displayed
          <input type="number" min="1" value={form.max_races ?? ""} onChange={(event) => updateField("max_races", event.target.value)} placeholder="All races (blank)" className={fieldClass} />
        </label>
        <label className={labelClass}>IOS Store Product ID
          <input
            value={form.ios_product_id || ""}
            onChange={(event) => handleIosIdChange(event.target.value)}
            className={fieldClass}
            placeholder="e.g. com.formguru.pro.monthly (Auto-filled)"
            maxLength={100}
          />
          <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)] font-normal normal-case -mt-1">
            <span>Apple App Store condition: max 100 characters</span>
            <span className={(form.ios_product_id || "").length >= 95 ? "text-[var(--color-accent-red)] font-bold" : ""}>
              {(form.ios_product_id || "").length} / 100
            </span>
          </div>
        </label>
        <label className={labelClass}>Android Store Product ID
          <input
            value={form.android_product_id || ""}
            onChange={(event) => handleAndroidIdChange(event.target.value)}
            className={fieldClass}
            placeholder="e.g. com.formguru.pro.monthly (Auto-filled)"
            maxLength={40}
          />
          <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)] font-normal normal-case -mt-1">
            <span>Android Play Store condition: max 40 characters</span>
            <span className={(form.android_product_id || "").length >= 38 ? "text-[var(--color-accent-red)] font-bold" : ""}>
              {(form.android_product_id || "").length} / 40
            </span>
          </div>
        </label>
      </div>

      <div className="grid gap-3">
        <div className="flex items-center justify-between gap-3">
          <p className="label-caps md:text-sm text-xs font-bold uppercase tracking-[0.04em] text-[var(--color-text-muted)]">Plan Features</p>
          <button
            type="button"
            onClick={addFeature}
            className="flex items-center justify-center w-7 h-7 rounded-full bg-[var(--color-primary)] border-none text-black cursor-pointer hover:opacity-85 transition-opacity"
            title="Add Feature"
          >
            <AddOutlined sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="grid gap-3">
          {(form.features || [""]).map((feature, index) => (
            <div key={index} className="flex items-center gap-3">
              <input
                value={feature}
                onChange={(event) => updateFeature(index, event.target.value)}
                placeholder={`Feature #${index + 1}`}
                className={fieldClass}
                required
              />
              <button
                type="button"
                onClick={() => removeFeature(index)}
                disabled={(form.features || [""]).length <= 1}
                className="min-w-10 min-h-10 flex items-center justify-center border border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] rounded-[var(--radius-md)] text-[var(--color-accent-red)] cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Remove Feature"
              >
                <DeleteOutlineOutlined sx={{ fontSize: 18 }} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <label className={labelClass}>Description
        <textarea value={form.description} onChange={(event) => updateField("description", event.target.value)} rows={3} className={`${fieldClass} resize-y leading-[1.5]`} placeholder="Describe what this plan offers" />
      </label>

      <div className="flex gap-4 flex-wrap">
        <label className="inline-flex items-center gap-2 text-[var(--color-text)] xl:text-base md:text-sm text-xs font-bold">
          <Switch checked={form.most_popular} onChange={(event) => updateField("most_popular", event.target.checked)} sx={switchSx} />
          Most Popular
        </label>
        <label className="inline-flex items-center gap-2 text-[var(--color-text)] xl:text-base md:text-sm text-xs font-bold">
          <Switch checked={form.is_active} onChange={(event) => updateField("is_active", event.target.checked)} sx={switchSx} />
          Active
        </label>
        <label className="inline-flex items-center gap-2 text-[var(--color-text)] xl:text-base md:text-sm text-xs font-bold">
          <Switch checked={form.show_hot_picks ?? true} onChange={(event) => updateField("show_hot_picks", event.target.checked)} sx={switchSx} />
          Show Hot Picks
        </label>
        {!editingPlan && (
          <label className="inline-flex items-center gap-2 text-[var(--color-text)] xl:text-base md:text-sm text-xs font-bold">
            <Switch checked={form.auto_provision ?? false} onChange={(event) => updateField("auto_provision", event.target.checked)} sx={switchSx} />
            Auto-Provision In Stores
          </label>
        )}
      </div>

      <button type="submit" disabled={saving} className={`inline-flex items-center justify-center justify-self-start gap-2 min-h-[42px] px-3 py-2.5 border border-[var(--color-primary)] rounded-[var(--radius-md)] bg-[var(--color-primary)] text-black md:text-sm text-xs font-black ${saving ? "cursor-not-allowed" : "cursor-pointer"}`}>
        {editingPlan ? <SaveOutlined sx={{ fontSize: 18 }} /> : <AddOutlined sx={{ fontSize: 18 }} />}
        {saving ? "Saving..." : editingPlan ? "Save plan" : "Add plan"}
      </button>
    </form>
  );
}

function PlansModule() {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(EMPTY_PLAN_FORM);
  const [editingPlan, setEditingPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyPlanId, setBusyPlanId] = useState(null);
  const [pricePoints, setPricePoints] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const formRef = useRef(null);
  const firstInputRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [plansData, pricePointsData] = await Promise.all([
        getSubscriptionPlans(),
        getSubscriptionPricePoints().catch(() => []) // Fallback to empty if price points fail
      ]);
      setPlans(plansData || []);
      
      let fetchedPrices = Array.isArray(pricePointsData) ? pricePointsData : [];
      // Ensure 0.00 is always an option for the Free plan
      if (!fetchedPrices.some(p => Number(p.customer_price) === 0)) {
        fetchedPrices = [
          {
            id: "apple_aus_0.00",
            customer_price: 0.00,
            customer_price_formatted: "$0.00",
            territory: "AUS",
          },
          ...fetchedPrices
        ];
      }
      setPricePoints(fetchedPrices);
    } catch (error) {
      setErrorMessage(getPlanErrorMessage(error, "Unable to load data."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);


  const resetForm = () => { setForm(EMPTY_PLAN_FORM); setEditingPlan(null); };

  const startEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name,
      description: plan.description || "",
      price: plan.price,
      currency: plan.currency,
      period_days: plan.period_days,
      max_races: plan.max_races !== null && plan.max_races !== undefined ? plan.max_races : "",
      is_active: plan.is_active,
      features: plan.features && plan.features.length ? plan.features : [""],
      most_popular: plan.most_popular || false,
      ios_product_id: plan.ios_product_id || "",
      android_product_id: plan.android_product_id || "",
      show_hot_picks: plan.show_hot_picks ?? true,
      auto_provision: plan.auto_provision ?? true,
    });
    setErrorMessage(""); setSuccessMessage("");
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      document.documentElement.scrollTo({ top: 0, behavior: "smooth" });
      document.body.scrollTo({ top: 0, behavior: "smooth" });
      const mainContent = document.querySelector("main") || document.querySelector(".dashboard-main-content") || document.querySelector(".admin-content-layout");
      if (mainContent) {
        mainContent.scrollTo({ top: 0, behavior: "smooth" });
      }
      firstInputRef.current?.focus({ preventScroll: true });
    }, 100);
  };

  const buildPayload = () => {
    const sanitizedFeatures = (form.features || [])
      .map((f) => f.trim())
      .filter(Boolean);

    const maxRacesValue = form.max_races !== "" && form.max_races !== null && form.max_races !== undefined
      ? Number(form.max_races)
      : null;

    return {
      ...form,
      price: Number(form.price || 0).toFixed(2),
      period_days: Number(form.period_days),
      currency: (form.currency || "AUD").toUpperCase(),
      max_races: maxRacesValue,
      features: sanitizedFeatures,
      most_popular: form.most_popular || false,
      ios_product_id: form.ios_product_id || "",
      android_product_id: form.android_product_id || "",
      show_hot_picks: form.show_hot_picks ?? true,
      auto_provision: form.auto_provision ?? true,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault(); setSaving(true); setErrorMessage(""); setSuccessMessage("");
    try {
      const savedPlan = editingPlan ? await updateSubscriptionPlan(editingPlan.id, buildPayload()) : await createSubscriptionPlan(buildPayload());
      setPlans((current) => {
        const isPopular = savedPlan.most_popular;
        if (editingPlan) {
          return current.map((plan) => {
            if (plan.id === savedPlan.id) {
              return savedPlan;
            }
            return isPopular ? { ...plan, most_popular: false } : plan;
          });
        }
        const updatedCurrent = isPopular
          ? current.map((plan) => ({ ...plan, most_popular: false }))
          : current;
        return [...updatedCurrent, savedPlan].sort((left, right) => Number(left.price) - Number(right.price));
      });
      let detailMsg = editingPlan ? "Plan updated." : "Plan added.";
      const appleCreated = savedPlan.apple_provision_status === "created";
      const googleCreated = savedPlan.google_provision_status === "created";
      const appleFailed = savedPlan.apple_provision_status?.startsWith("failed:");
      const googleFailed = savedPlan.google_provision_status?.startsWith("failed:");

      if (appleCreated || googleCreated) {
        const appleMsg = appleCreated
          ? `Apple App Store (non-EU countries equalized, screenshot ${savedPlan.apple_screenshot_status || "uploaded"})`
          : null;
        const googleMsg = googleCreated
          ? "Google Play (non-EU countries converted)"
          : null;
        const stores = [appleMsg, googleMsg].filter(Boolean).join(" & ");
        detailMsg = `Plan created and provisioned on ${stores}.`;
      }

      if (appleFailed || googleFailed) {
        const storeErrors = [];
        if (appleFailed) {
          storeErrors.push(`Apple: ${savedPlan.apple_provision_status.replace("failed:", "").trim()}`);
        }
        if (googleFailed) {
          storeErrors.push(`Google Play: ${savedPlan.google_provision_status.replace("failed:", "").trim()}`);
        }
        setErrorMessage(`Plan saved in database, but store provisioning failed: ${storeErrors.join(" | ")}`);
      } else {
        setSuccessMessage(detailMsg);
      }
      resetForm();
    } catch (error) {
      setErrorMessage(getPlanErrorMessage(error, "Unable to save subscription plan."));
    } finally { setSaving(false); }
  };

  const togglePlanStatus = async (plan) => {
    setBusyPlanId(plan.id); setErrorMessage(""); setSuccessMessage("");
    try {
      const updatedPlan = await updateSubscriptionPlan(plan.id, { is_active: !plan.is_active });
      setPlans((current) => current.map((item) => (item.id === updatedPlan.id ? updatedPlan : item)));
      setSuccessMessage(updatedPlan.is_active ? "Plan activated." : "Plan deactivated.");
    } catch (error) {
      setErrorMessage(getPlanErrorMessage(error, "Unable to update plan status."));
    } finally { setBusyPlanId(null); }
  };

  return (
    <div className="grid gap-4">
      <PlanForm form={form} setForm={setForm} editingPlan={editingPlan} saving={saving} onSubmit={handleSubmit} onCancel={resetForm} formRef={formRef} firstInputRef={firstInputRef} pricePoints={pricePoints} />

      {(errorMessage || successMessage) && (
        <div className={`px-4 py-3.5 border rounded-[var(--radius-md)] md:text-sm text-xs ${errorMessage ? "border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] text-[var(--color-accent-red)]" : "border-[rgba(200,255,0,0.25)] bg-[rgba(200,255,0,0.12)] text-[var(--color-primary)]"}`}>
          {errorMessage || successMessage}
        </div>
      )}

      <div className="border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)] overflow-hidden">
        {loading ? (
          <div className="grid gap-px">{[1, 2, 3].map((item) => <div key={item} className="skeleton h-[70px] bg-[rgba(255,255,255,0.04)]" />)}</div>
        ) : plans.length === 0 ? (
          <div className="min-h-[180px] grid place-items-center xl:text-xl md:text-lg text-base text-[var(--color-text-muted)] text-center">No subscription plans found.</div>
        ) : (
          <div className="flex flex-col lg:grid lg:grid-cols-[1fr_auto_auto_auto]">
            {plans.map((plan, idx) => (
              <div key={plan.id} className={`flex flex-col lg:contents ${idx !== 0 ? "border-t border-[var(--color-border)] lg:border-t-0" : ""}`}>
                <div className={`min-w-0 flex flex-col gap-1 justify-center px-4 pt-3 pb-2 lg:py-0 lg:min-h-[74px] ${idx !== 0 ? "lg:border-t lg:border-[var(--color-border)]" : ""}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h5 className="m-0 xl:text-lg md:text-base text-sm text-[var(--color-text)] font-bold leading-none">{plan.name}</h5>
                    {plan.most_popular && (
                      <span className="inline-flex items-center px-2 py-1 rounded-[var(--radius-md)] bg-[rgba(200,255,0,0.12)] border border-[rgba(200,255,0,0.25)] text-[var(--color-primary)] text-[10px] font-bold uppercase tracking-[0.04em]">
                        Popular
                      </span>
                    )}
                  </div>
                  <p className="m-0 text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs leading-[1.5]">{plan.period_days} days | {plan.max_races != null ? `${plan.max_races} races` : "All races"}</p>
                </div>
                <div className="flex items-center gap-3 px-4 pb-3 lg:contents">
                  <div className={`flex items-center lg:justify-start lg:min-h-[74px] lg:px-4 ${idx !== 0 ? "lg:border-t lg:border-[var(--color-border)]" : ""}`}>
                    <p className="tabular m-0 text-[var(--color-text)] xl:text-lg md:text-base text-sm font-black whitespace-nowrap">{formatPlanPrice(plan)}</p>
                  </div>
                  <div className={`flex items-center gap-2 lg:min-h-[74px] lg:px-4 ${idx !== 0 ? "lg:border-t lg:border-[var(--color-border)]" : ""}`}>
                    <PlanStatusPill active={plan.is_active} />
                    <Switch checked={plan.is_active} disabled={busyPlanId === plan.id} onChange={() => togglePlanStatus(plan)} inputProps={{ "aria-label": `${plan.is_active ? "Deactivate" : "Activate"} ${plan.name}` }} sx={switchSx} />
                  </div>
                  <div className={`ml-auto lg:ml-0 flex gap-2 justify-end items-center lg:min-h-[74px] lg:px-4 ${idx !== 0 ? "lg:border-t lg:border-[var(--color-border)]" : ""}`}>
                    <button type="button" onClick={() => startEdit(plan)} disabled={busyPlanId === plan.id} aria-label={`Edit ${plan.name}`} title="Edit" className={`w-10 h-10 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-transparent text-[var(--color-text)] ${busyPlanId === plan.id ? "cursor-not-allowed" : "cursor-pointer"}`}>
                      <EditOutlined sx={{ fontSize: 18 }} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

export default PlansModule;
